import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Sprout, UserPlus, X, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';
import { getWeekRange as getWeekRangeUtil } from '../lib/weekUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LancamentoSemanal = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ presenca: 5, pontualidade: 5, biblia: 5, visita: 10, versiculo: 3 });
  const [formData, setFormData] = useState({
    semana: (() => { const n = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
    child_id: '',
    presenca: false,
    pontualidade: false,
    biblia: false,
    visita: 0,
    versiculo: false,
    gincana: 0,
    devocional: 0,
    devocional_semana: '',
    se_vira: 0,
    observacao: ''
  });
  const [visitors, setVisitors] = useState([]);
  const [showVisitorSection, setShowVisitorSection] = useState(false);
  const [devocionalInfo, setDevocionalInfo] = useState(null);
  const [newVisitor, setNewVisitor] = useState({ nome_visitante: '', celular_responsavel: '', sexo: '', idade: '' });
  const [childPickerOpen, setChildPickerOpen] = useState(false);

  useEffect(() => {
    fetchChildren();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try { const res = await axios.get(`${API}/settings`); setSettings(res.data); } catch {}
  };

  const fetchChildren = async () => {
    try { const res = await axios.get(`${API}/children`); setChildren(res.data); } catch {}
  };

  const getWeekRange = (weekNum) => {
    return getWeekRangeUtil(weekNum, new Date().getFullYear());
  };

  const fetchDevocionalInfo = async (weekNum) => {
    if (!formData.child_id || !weekNum) { setDevocionalInfo(null); return; }
    const { sunday, saturday } = getWeekRange(weekNum);
    const ws = sunday.toISOString().split('T')[0];
    const we = saturday.toISOString().split('T')[0];
    try {
      const res = await axios.get(`${API}/activities/devocional-week?child_id=${formData.child_id}&week_start=${ws}&week_end=${we}`);
      setDevocionalInfo(res.data);
      if (formData.devocional > res.data.disponivel) {
        setFormData(prev => ({ ...prev, devocional: res.data.disponivel }));
      }
    } catch { setDevocionalInfo(null); }
  };

  const calculatePoints = () => {
    let points = 0;
    if (formData.presenca) points += settings.presenca;
    if (formData.pontualidade) points += settings.pontualidade;
    if (formData.biblia) points += settings.biblia;
    if (formData.visita) points += settings.visita * formData.visita;
    if (formData.versiculo) points += settings.versiculo;
    points += parseInt(formData.gincana) || 0;
    points += parseInt(formData.devocional) || 0;
    points += parseInt(formData.se_vira) || 0;
    return points;
  };

  const addVisitor = () => {
    if (!newVisitor.nome_visitante.trim()) { toast.error('Informe o nome do visitante'); return; }
    const updated = [...visitors, { ...newVisitor }];
    setVisitors(updated);
    setFormData(prev => ({ ...prev, visita: updated.length }));
    setNewVisitor({ nome_visitante: '', celular_responsavel: '', sexo: '', idade: '' });
  };

  const removeVisitor = (index) => {
    const updated = visitors.filter((_, i) => i !== index);
    setVisitors(updated);
    setFormData(prev => ({ ...prev, visita: updated.length }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.child_id) { toast.error('Selecione uma crianca'); return; }

    setLoading(true);
    try {
      // Save weekly activity
      const { devocional_semana, ...payload } = formData;
      await axios.post(`${API}/activities/weekly`, {
        ...payload,
        gincana: parseInt(payload.gincana) || 0,
        devocional: parseInt(payload.devocional) || 0,
        se_vira: parseInt(payload.se_vira) || 0
      }, { headers: getAdminHeaders() });

      // Register visitors if any
      if (formData.visita > 0 && visitors.length > 0) {
        for (const v of visitors) {
          await axios.post(`${API}/visitors`, {
            nome_visitante: v.nome_visitante,
            semana: formData.semana,
            convidado_por: formData.child_id,
            celular_responsavel: v.celular_responsavel,
            sexo: v.sexo || '',
            idade: v.idade ? parseInt(v.idade) : null,
          }, { headers: getAdminHeaders() });
        }
      }

      toast.success('Lançamento semanal registrado!');
      setFormData({
        semana: formData.semana,
        child_id: '',
        presenca: false, pontualidade: false, biblia: false,
        visita: 0, versiculo: false, gincana: 0, devocional: 0, devocional_semana: '', se_vira: 0, observacao: ''
      });
      setVisitors([]);
      setShowVisitorSection(false);
      setDevocionalInfo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar');
    } finally { setLoading(false); }
  };

  const selectedChild = children.find(c => c.id === formData.child_id);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button data-testid="back-button" onClick={() => navigate(-1)} className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Lançamento Semanal</h1>
            </div>
            <img src="/logos/logo_semente.png" alt="Projeto Semente" className="w-36 h-36 drop-shadow-lg" />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Semana */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Semana/Data</Label>
              <Input type="date" data-testid="input-semana" value={formData.semana}
                max={(() => { const n = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })()}
                onChange={(e) => {
                  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
                  const selected = new Date(e.target.value + 'T12:00:00');
                  if (selected > today) { toast.error('Não é permitido lançar sementes com data futura'); return; }
                  setFormData(prev => ({ ...prev, semana: e.target.value }));
                }}
                className="rounded-xl border-gray-200 bg-gray-50/50" required />
            </div>

            {/* Crianca */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Crianca *</Label>
              <Popover open={childPickerOpen} onOpenChange={setChildPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={childPickerOpen}
                    data-testid="select-child"
                    className="w-full justify-between rounded-xl border-gray-200 bg-gray-50/50 font-normal text-left h-10"
                  >
                    {formData.child_id ? (() => {
                      const c = children.find(c => c.id === formData.child_id);
                      return c ? `#${c.numero || 0} ${c.nome}` : 'Selecione uma criança';
                    })() : <span className="text-gray-500">Selecione uma criança</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[calc(100vw-2rem)] sm:w-[28rem] p-0" align="start">
                  <Command
                    filter={(value, search) => {
                      // value is the lowercase concat of child name + numero
                      const s = search.trim().toLowerCase();
                      if (!s) return 1;
                      return value.includes(s) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder="Buscar pelo nome..." data-testid="input-search-child" className="h-10" />
                    <CommandList className="max-h-72">
                      <CommandEmpty>Nenhuma criança encontrada.</CommandEmpty>
                      <CommandGroup>
                        {children.map((child) => {
                          const searchKey = `${child.numero || 0} ${(child.nome || '').toLowerCase()}`;
                          return (
                            <CommandItem
                              key={child.id}
                              value={searchKey}
                              data-testid={`child-option-${child.id}`}
                              onSelect={() => {
                                setFormData(prev => ({ ...prev, child_id: child.id }));
                                setChildPickerOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${formData.child_id === child.id ? 'opacity-100' : 'opacity-0'}`}
                              />
                              <span className="text-gray-500 text-xs mr-2">#{child.numero || 0}</span>
                              <span>{child.nome}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Switches */}
            {[
              { key: 'presenca', label: 'Presenca', pts: settings.presenca },
              { key: 'pontualidade', label: 'Pontualidade', pts: settings.pontualidade },
              { key: 'biblia', label: 'Biblia', pts: settings.biblia },
              { key: 'versiculo', label: 'Versículo', pts: settings.versiculo },
            ].map(({ key, label, pts }) => (
              <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <Label className="font-inter font-semibold text-[#1A4D2E] cursor-pointer">{label}</Label>
                  <p className="font-inter text-sm text-gray-500">+{pts} sementes</p>
                </div>
                <Switch data-testid={`switch-${key}`} checked={formData[key]}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, [key]: checked }))}
                  className="data-[state=checked]:bg-[#1A4D2E]" />
              </div>
            ))}

            {/* Switch Trouxe Visitante */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] cursor-pointer">Trouxe Visitante</Label>
                <p className="font-inter text-sm text-gray-500">+{settings.visita} sementes por visitante</p>
              </div>
              <Switch data-testid="switch-visita" checked={showVisitorSection}
                onCheckedChange={(checked) => {
                  setShowVisitorSection(checked);
                  if (!checked) { setVisitors([]); setFormData(prev => ({ ...prev, visita: 0 })); }
                }}
                className="data-[state=checked]:bg-[#1A4D2E]" />
            </div>

            {/* Visitantes - aparece quando "Trouxe Visitante" é marcado */}
            {showVisitorSection && (
              <Card className="bg-blue-50 rounded-xl p-4 border border-blue-200 space-y-3">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  <h4 className="font-inter font-semibold text-blue-700">Cadastrar Visitante(s)</h4>
                </div>

                {/* Lista de visitantes adicionados */}
                {visitors.map((v, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-lg p-2.5 border border-blue-100">
                    <div>
                      <p className="font-inter text-sm font-semibold text-[#1A4D2E]">{v.nome_visitante}</p>
                      <div className="flex gap-2 mt-0.5">
                        {v.sexo && <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${v.sexo === 'M' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'}`}>{v.sexo === 'M' ? 'Menino' : 'Menina'}</span>}
                        {v.idade && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-semibold">{v.idade} anos</span>}
                        {v.celular_responsavel && <span className="font-inter text-xs text-gray-400">{v.celular_responsavel}</span>}
                      </div>
                    </div>
                    <button type="button" onClick={() => removeVisitor(i)} className="text-red-400 hover:text-red-600 p-1" data-testid={`remove-visitor-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Form para novo visitante */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <Input data-testid="input-visitor-name" placeholder="Nome do visitante *"
                    value={newVisitor.nome_visitante}
                    onChange={(e) => setNewVisitor(prev => ({ ...prev, nome_visitante: e.target.value }))}
                    className="rounded-lg border-blue-200 bg-white col-span-2 md:col-span-1 text-sm" />
                  <select data-testid="input-visitor-sexo" value={newVisitor.sexo}
                    onChange={(e) => setNewVisitor(prev => ({ ...prev, sexo: e.target.value }))}
                    className="rounded-lg border border-blue-200 bg-white h-10 px-3 text-sm font-inter">
                    <option value="">Sexo</option>
                    <option value="M">Menino</option>
                    <option value="F">Menina</option>
                  </select>
                  <Input data-testid="input-visitor-idade" type="number" placeholder="Idade" min="0" max="18"
                    value={newVisitor.idade}
                    onChange={(e) => setNewVisitor(prev => ({ ...prev, idade: e.target.value }))}
                    className="rounded-lg border-blue-200 bg-white text-sm" />
                  <Input data-testid="input-visitor-phone" placeholder="Celular responsável"
                    value={newVisitor.celular_responsavel}
                    onChange={(e) => setNewVisitor(prev => ({ ...prev, celular_responsavel: e.target.value }))}
                    className="rounded-lg border-blue-200 bg-white text-sm" />
                  <Button type="button" data-testid="btn-add-visitor" onClick={addVisitor}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm col-span-2 md:col-span-1">
                    <UserPlus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                </div>

                {selectedChild && visitors.length > 0 && (
                  <p className="font-inter text-xs text-blue-600">
                    {visitors.length} visitante(s) convidado(s) por {selectedChild.nome}
                  </p>
                )}
              </Card>
            )}

            {/* Gincana */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Pontos de Gincana</Label>
              <Input type="number" min="0" data-testid="input-gincana" value={formData.gincana}
                onChange={(e) => setFormData(prev => ({ ...prev, gincana: e.target.value }))}
                className="rounded-xl border-gray-200 bg-gray-50/50" placeholder="0" />
            </div>

            {/* Devocional */}
            <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-200/50 space-y-3">
              <Label className="font-inter font-semibold text-[#1A4D2E] block">Sementes de Devocional</Label>

              {/* Seletor de Semana */}
              <div>
                <label className="font-inter text-xs text-gray-500 block mb-1">Semana do Ano</label>
                <select
                  data-testid="select-devocional-semana"
                  value={formData.devocional_semana || ''}
                  onChange={(e) => {
                    const weekNum = parseInt(e.target.value);
                    setFormData(prev => ({ ...prev, devocional_semana: weekNum }));
                    fetchDevocionalInfo(weekNum);
                  }}
                  className="w-full rounded-xl border border-gray-200 bg-white p-2.5 text-sm focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E]"
                >
                  <option value="">Selecione a semana...</option>
                  {Array.from({ length: 52 }, (_, i) => {
                    const weekNum = i + 1;
                    const { sunday, saturday } = getWeekRangeUtil(weekNum, new Date().getFullYear());
                    const today = new Date();
                    if (sunday > today) return null;
                    const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
                    return (
                      <option key={weekNum} value={weekNum}>
                        Semana {weekNum} ({fmt(sunday)} - {fmt(saturday)})
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Período */}
              {formData.devocional_semana && (
                <p className="text-xs text-amber-700 font-medium">
                  {(() => {
                    const { sunday, saturday } = getWeekRangeUtil(formData.devocional_semana, new Date().getFullYear());
                    const fmt = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                    return `Período: ${fmt(sunday)} (dom) a ${fmt(saturday)} (sáb)`;
                  })()}
                </p>
              )}

              {/* Info de sementes já lançadas */}
              {devocionalInfo && formData.devocional_semana && (
                <div className="bg-white rounded-lg p-2.5 border border-amber-200/70 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Máximo semanal:</span>
                    <span className="font-semibold text-[#1A4D2E]">{devocionalInfo.max}</span>
                  </div>
                  {devocionalInfo.ja_lancado > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Já lançado pelo pai:</span>
                      <span className="font-semibold text-amber-600">-{devocionalInfo.ja_lancado}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs border-t border-amber-100 pt-1">
                    <span className="text-gray-500 font-semibold">Disponível:</span>
                    <span className="font-bold text-[#1A4D2E]">{devocionalInfo.disponivel}</span>
                  </div>
                </div>
              )}

              {/* Input de sementes */}
              <div>
                <label className="font-inter text-xs text-gray-500 block mb-1">
                  Quantidade de sementes (máx: {devocionalInfo ? devocionalInfo.disponivel : (settings.devocional || 2) * 7})
                </label>
                <Input
                  type="number"
                  min="0"
                  max={devocionalInfo ? devocionalInfo.disponivel : (settings.devocional || 2) * 7}
                  data-testid="input-devocional"
                  value={formData.devocional}
                  onChange={(e) => {
                    const maxVal = devocionalInfo ? devocionalInfo.disponivel : (settings.devocional || 2) * 7;
                    let val = parseInt(e.target.value) || 0;
                    if (val > maxVal) val = maxVal;
                    setFormData(prev => ({ ...prev, devocional: val }));
                  }}
                  className="rounded-xl border-gray-200 bg-white"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Se Vira nos 5 */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Pontos de Se Vira nos 5</Label>
              <Input type="number" min="0" data-testid="input-se-vira" value={formData.se_vira}
                onChange={(e) => setFormData(prev => ({ ...prev, se_vira: e.target.value }))}
                className="rounded-xl border-gray-200 bg-gray-50/50" placeholder="0" />
            </div>

            {/* Observação */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Observação</Label>
              <Textarea data-testid="input-observacao" value={formData.observacao}
                onChange={(e) => setFormData(prev => ({ ...prev, observacao: e.target.value }))}
                className="rounded-xl border-gray-200 bg-gray-50/50 min-h-[80px]" placeholder="Anotações..." />
            </div>

            {/* Pontos */}
            <Card className="bg-gradient-to-r from-[#FEFAE6] to-[#F2F7F4] rounded-xl p-6 border border-[#F4D35E]/40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sprout className="w-8 h-8 text-[#F4D35E]" />
                  <div>
                    <p className="font-inter text-sm text-gray-600">Pontos da Semana</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">{calculatePoints()}</p>
                  </div>
                </div>
                <p className="font-inter text-sm text-gray-500">sementes</p>
              </div>
            </Card>

            <Button type="submit" data-testid="submit-button" disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all">
              {loading ? 'Salvando...' : 'Salvar Lançamento'}
            </Button>
          </form>
        </Card>
      </div>
      <Navigation active="lancamento" />
    </div>
  );
};

export default LancamentoSemanal;
